import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { GlobalVariable } from '../helpers/settings';
import { Result } from '../models/general';
import { PaginationOption } from '../models/pagination.model';
import { TimeTable } from '../models/time-table-model';
import { UserService } from './user.service';
import { RefreshVerificationService } from './refresh-verification.service';

export interface PaginationData {
	docs: TimeTable[];
	total: number;
	limit: number;
	page: number;
	pages: number;
}

@Injectable({
	providedIn: 'root'
})
export class TimeTableService {
	readonly BASE_URL = `${GlobalVariable.server.host}/time-tables`;
	private previousComponent: string = '';

	constructor(private httpClient: HttpClient, private refreshVerifService: RefreshVerificationService) {}

	find(
		paginationOption: PaginationOption,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string,
		action: string,
		attribute?: string,
		projectId: string = null
	): Observable<PaginationData> {
		let params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName)
			.set('actionName', action)
			.set('attribute', attribute);
		if (projectId) {
			params = params.set('project', projectId);
		}
		return this.refreshVerifService.buidResult(
			this.httpClient.post<PaginationData>(
				`${this.BASE_URL}/find`,
				{
					paginationOption
				},
				{ params }
			)
		);
	}

	findById(
		id: string,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string,
		action: string
	): Observable<TimeTable> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName)
			.set('actionName', action);
		return this.refreshVerifService.buidResult(
			this.httpClient.get<TimeTable>(`${this.BASE_URL}/find-by-id/${id}`, { params })
		);
	}

	getTimeTableByProjectId(
		projectId: string,
		isChildComponent: boolean,
		fromAppComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<TimeTable[]> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('fromAppComponent', fromAppComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName)
			.set('projectId', projectId);
		return this.refreshVerifService.buidResult(
			this.httpClient.get<TimeTable[]>(`${this.BASE_URL}/get-time-table-models-by-project-id/${projectId}`, {
				params
			})
		);
	}

	duplicate(
		timeTable: Partial<TimeTable>,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<{ _id?: string }> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult(
			this.httpClient.post<{ _id: string }>(`${this.BASE_URL}/duplicate`, timeTable, { params })
		);
	}

	create(
		timeTable: Partial<TimeTable>,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<{ _id?: string }> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult(
			this.httpClient.post<{ _id: string }>(`${this.BASE_URL}/create`, timeTable, { params })
		);
	}

	edit(
		timeTable: Partial<TimeTable>,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<Result> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult(
			this.httpClient.put<Result>(`${this.BASE_URL}/edit`, timeTable, { params })
		);
	}

	delete(
		productId: string,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<Result> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult(
			this.httpClient.delete<Result>(`${this.BASE_URL}/delete/${productId}`, { params })
		);
	}

	archive(
		productId: string,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<Result> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult(
			this.httpClient.delete<Result>(`${this.BASE_URL}/archive/${productId}`, { params })
		);
	}

	isUsed(
		productId: string,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<Result> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult(
			this.httpClient.get<Result>(`${this.BASE_URL}/if-is-Used/${productId}`, { params })
		);
	}

	setPreviousComponent(componentName: string) {
		this.previousComponent = componentName;
	}

	getPreviousComponent(): string {
		const currentModel = this.previousComponent;
		this.previousComponent = '';
		return currentModel;
	}
}
